import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/constants";
import api, { getErrorMessage } from "@/lib/api";

function stripExtension(filename) {
  if (!filename) return "";
  const lastDot = filename.lastIndexOf(".");
  if (lastDot <= 0) return filename;
  return filename.slice(0, lastDot);
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

// ---------------------------------------------------------------------------
// NotConnectedBanner — shown when OAuth is not set up or expired
// ---------------------------------------------------------------------------

function NotConnectedBanner({ expired, onConnect }) {
  const message = expired
    ? "Outlook connection expired — reconnect required."
    : "Outlook is not connected.";

  return (
    <div
      data-testid="oauth-not-connected-banner"
      className="flex items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm"
    >
      <span className="text-amber-800">{message}</span>
      <Button
        type="button"
        size="sm"
        className="shrink-0 text-white"
        style={{ backgroundColor: BRAND.bg }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BRAND.hover)}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BRAND.bg)}
        onClick={onConnect}
      >
        Connect Outlook
      </Button>
    </div>
  );
}

export default function SendEmailDialog({ open, onOpenChange, doc, docs, onSent }) {
  // Normalize to a list of docs
  const batchDocs = docs && docs.length ? docs : doc ? [doc] : [];
  const isBatch = batchDocs.length > 1;
  const primaryDoc = batchDocs[0] || null;

  // OAuth status
  const [oauthStatus, setOauthStatus] = useState(null); // null = loading
  const [oauthExpired, setOauthExpired] = useState(false);
  // Phase 6 #102: per-tenant sender_mode controls which transport is used.
  // donotreply -> ACS (no OAuth required); outlook -> Graph (requires OAuth).
  const [senderMode, setSenderMode] = useState("donotreply");

  // Contacts from API. Groups intentionally NOT fetched here — this dialog
  // is for ad-hoc per-doc sends; group-based routing belongs in the
  // address book (contact creation) and the rules engine.
  const [contacts, setContacts] = useState([]);

  // Recipient selections
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [adHocInput, setAdHocInput] = useState("");
  const [adHocEmails, setAdHocEmails] = useState([]);
  const [adHocError, setAdHocError] = useState("");

  // Form fields
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  // Submission state
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !primaryDoc) return;

    // Reset form when dialog opens
    setSelectedContactIds([]);
    setAdHocInput("");
    setAdHocEmails([]);
    setAdHocError("");
    setMessage("");
    setOauthStatus(null);
    setOauthExpired(false);
    setSenderMode("donotreply");

    // Subject default
    if (isBatch) {
      const firstName = stripExtension(
        primaryDoc.original_filename || primaryDoc.filename || primaryDoc.blob_name || ""
      );
      setSubject(`${firstName} + ${batchDocs.length - 1} more`);
    } else {
      setSubject(
        stripExtension(
          primaryDoc.original_filename || primaryDoc.filename || primaryDoc.blob_name || ""
        )
      );
    }

    // Fetch oauth status + contacts + groups in parallel
    api.get("/email/oauth/status")
      .then((res) => {
        setOauthStatus(res.data?.status ?? "not_connected");
        setSenderMode(res.data?.sender_mode ?? "donotreply");
      })
      .catch(() => setOauthStatus("not_connected"));

    api.get("/email/contacts")
      .then((res) => setContacts(res.data || []))
      .catch(() => setContacts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, primaryDoc]);

  if (!primaryDoc) return null;

  const filename = primaryDoc.original_filename || primaryDoc.filename || primaryDoc.blob_name || "";

  // Is OAuth connected?
  const oauthConnected = oauthStatus === "connected";
  const oauthLoading = oauthStatus === null;

  // Phase 6 #102: in donotreply mode, sending requires no OAuth — ACS handles it.
  // In outlook mode, the connected mailbox is mandatory.
  const isDonotreplyMode = senderMode === "donotreply";
  const canSendInCurrentMode = isDonotreplyMode || oauthConnected;

  // Show the "Outlook not connected" banner only when outlook mode + not connected.
  // In donotreply mode, the lack of an Outlook connection is irrelevant.
  const showNotConnectedBanner = !oauthLoading && !isDonotreplyMode && !oauthConnected;

  function handleConnectOutlook() {
    api.get("/email/oauth/authorize")
      .then((res) => {
        window.location.href = res.data.authorize_url;
      })
      .catch(() => {
        toast.error("Failed to start the Outlook connection. Please try again.");
      });
  }

  // Validate and add ad-hoc emails from the free-text field
  const commitAdHoc = () => {
    const raw = adHocInput.trim();
    if (!raw) return;
    const parts = raw.split(/[\s,]+/).filter(Boolean);
    const invalid = parts.filter((e) => !isValidEmail(e));
    if (invalid.length > 0) {
      setAdHocError(`Invalid email(s): ${invalid.join(", ")}`);
      return;
    }
    setAdHocEmails((prev) => {
      const merged = [...new Set([...prev, ...parts])];
      return merged;
    });
    setAdHocInput("");
    setAdHocError("");
  };

  const removeAdHoc = (email) => {
    setAdHocEmails((prev) => prev.filter((e) => e !== email));
  };

  const toggleContact = (id) => {
    setSelectedContactIds((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const hasRecipients =
    selectedContactIds.length > 0 || adHocEmails.length > 0;

  // Send is disabled when: no recipients, no subject, can't send in current mode, or submitting.
  // canSendInCurrentMode is true in donotreply mode regardless of OAuth.
  const canSubmit = hasRecipients && subject.trim().length > 0 && canSendInCurrentMode && !submitting;

  const handleClose = (isOpen) => {
    if (!isOpen) {
      // Reset state on close
      setSelectedContactIds([]);
      setAdHocInput("");
      setAdHocEmails([]);
      setAdHocError("");
      setSubject("");
      setMessage("");
      setOauthStatus(null);
      setOauthExpired(false);
      setSenderMode("donotreply");
    }
    onOpenChange(isOpen);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Commit any uncommitted ad-hoc input before submit
    const raw = adHocInput.trim();
    let finalAdHoc = [...adHocEmails];
    if (raw) {
      const parts = raw.split(/[\s,]+/).filter(Boolean);
      const invalid = parts.filter((em) => !isValidEmail(em));
      if (invalid.length > 0) {
        setAdHocError(`Invalid email(s): ${invalid.join(", ")}`);
        return;
      }
      finalAdHoc = [...new Set([...finalAdHoc, ...parts])];
      setAdHocEmails(finalAdHoc);
      setAdHocInput("");
      setAdHocError("");
    }

    if (selectedContactIds.length === 0 && finalAdHoc.length === 0) {
      toast.error("Please add at least one recipient.");
      return;
    }

    setSubmitting(true);
    try {
      let res;
      if (isBatch) {
        res = await api.post("/documents/send-email-batch", {
          blob_names: batchDocs.map((d) => d.blob_name),
          to_emails: finalAdHoc,
          contact_ids: selectedContactIds,
          subject: subject.trim(),
          message,
        });
      } else {
        res = await api.post(
          `/documents/${encodeURIComponent(primaryDoc.blob_name)}/send-email`,
          {
            to_emails: finalAdHoc,
            contact_ids: selectedContactIds,
            subject: subject.trim(),
            message,
          }
        );
      }

      const status = res.data?.status;
      const count = res.data?.recipient_count ?? "?";
      const attachCount = res.data?.attachment_count;

      if (status === "dev_mode") {
        toast.warning(
          "Email saved to dev log only — ACS not configured in this environment. No email was actually sent."
        );
      } else if (isBatch && attachCount != null) {
        toast.success(`Email sent to ${count} recipient(s) with ${attachCount} attachment(s).`);
      } else {
        toast.success(`Email sent to ${count} recipient(s).`);
      }

      // Reset and close (both sent and dev_mode close the dialog)
      handleClose(false);
      onSent?.();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;

      if (status === 409) {
        // OAuth not connected or expired — show inline banner, keep dialog open
        const reason = typeof detail === "object" ? detail?.reason : null;
        if (reason === "oauth_expired") {
          setOauthStatus("expired");
          setOauthExpired(true);
        } else {
          // oauth_required or any other 409
          setOauthStatus("not_connected");
          setOauthExpired(false);
        }
        // Don't toast — the banner is the error signal
        return;
      }

      if (status === 400) {
        toast.error(getErrorMessage(err, "Bad request."));
      } else if (status === 404) {
        if (isBatch) {
          toast.error("One or more documents could not be found.");
        } else {
          toast.error("Document not found.");
        }
      } else if (status === 413) {
        if (isBatch) {
          toast.error("Combined attachment size exceeds 9 MB. Send fewer or smaller documents.");
        } else {
          toast.error("Attachment is too large (>9 MB). Please download and attach manually.");
        }
      } else {
        toast.error("Send failed. Please try again.");
      }
      // Keep dialog open on error
    } finally {
      setSubmitting(false);
    }
  };

  // For batch mode: show up to 5 filenames
  const visibleDocs = isBatch ? batchDocs.slice(0, 5) : [];
  const hiddenCount = isBatch ? batchDocs.length - visibleDocs.length : 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          {isBatch ? (
            <DialogTitle>Send {batchDocs.length} documents by email</DialogTitle>
          ) : (
            <DialogTitle>Send &quot;{filename}&quot; by email</DialogTitle>
          )}
          <DialogDescription>
            This sends the document as a PDF attachment. Replies will go to your email.
          </DialogDescription>
        </DialogHeader>

        {/* Not-connected banner */}
        {showNotConnectedBanner && (
          <NotConnectedBanner
            expired={oauthExpired || oauthStatus === "expired"}
            onConnect={handleConnectOutlook}
          />
        )}

        {/* Batch document list */}
        {isBatch && (
          <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600 space-y-0.5">
            {visibleDocs.map((d) => (
              <div key={d.blob_name} className="truncate">
                {d.original_filename || d.filename || d.blob_name}
              </div>
            ))}
            {hiddenCount > 0 && (
              <div className="text-gray-400">+ {hiddenCount} more</div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Contacts multi-select */}
          {contacts.length > 0 && (
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Contacts</Label>
              <div className="max-h-32 overflow-y-auto border rounded-md divide-y divide-gray-100">
                {contacts.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-gray-50 text-xs"
                  >
                    <input
                      type="checkbox"
                      className="rounded border-gray-300"
                      checked={selectedContactIds.includes(c.id)}
                      onChange={() => toggleContact(c.id)}
                      disabled={submitting || showNotConnectedBanner}
                    />
                    <span className="text-gray-800">{c.name}</span>
                    <span className="text-gray-400">&lt;{c.email}&gt;</span>
                    {c.group_name && (
                      <span
                        className="text-xs px-1.5 py-0.5 rounded-full ml-1"
                        style={{ backgroundColor: BRAND.soft, color: BRAND.text }}
                      >
                        {c.group_name}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Ad-hoc emails */}
          <div className="space-y-1">
            <Label htmlFor="adhoc-emails" className="text-xs text-gray-500">
              Additional recipients (comma-separated emails)
            </Label>
            {/* Pills */}
            {adHocEmails.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-1">
                {adHocEmails.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: BRAND.soft, color: BRAND.text }}
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => removeAdHoc(email)}
                      disabled={submitting || showNotConnectedBanner}
                      className="hover:opacity-70 leading-none"
                      aria-label={`Remove ${email}`}
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            )}
            <Input
              id="adhoc-emails"
              type="text"
              placeholder="someone@example.com, another@example.com"
              value={adHocInput}
              onChange={(e) => {
                setAdHocInput(e.target.value);
                setAdHocError("");
              }}
              onBlur={commitAdHoc}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === "," || e.key === " ") {
                  e.preventDefault();
                  commitAdHoc();
                }
              }}
              disabled={submitting || showNotConnectedBanner}
              className="text-xs"
            />
            {adHocError && (
              <p className="text-xs text-red-500">{adHocError}</p>
            )}
          </div>

          {/* Subject */}
          <div className="space-y-1">
            <Label htmlFor="email-subject">Subject</Label>
            <Input
              id="email-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={255}
              required
              disabled={submitting || showNotConnectedBanner}
              placeholder="Email subject"
            />
          </div>

          {/* Message */}
          <div className="space-y-1">
            <Label htmlFor="email-message">Message</Label>
            <Textarea
              id="email-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              maxLength={50000}
              disabled={submitting || showNotConnectedBanner}
              placeholder="Optional message body..."
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit}
              className="text-white"
              style={{ backgroundColor: BRAND.bg }}
              onMouseEnter={(e) => {
                if (!submitting) e.currentTarget.style.backgroundColor = BRAND.hover;
              }}
              onMouseLeave={(e) => {
                if (!submitting) e.currentTarget.style.backgroundColor = BRAND.bg;
              }}
            >
              {submitting ? (
                <span className="flex items-center gap-1">
                  <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Sending...
                </span>
              ) : (
                "Send"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
